import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, ChevronRight, Music, GlassWater, ConciergeBell, Sparkles, Timer, Smile, FileText, Zap, PartyPopper, User, Building2, Users, ClipboardList, Handshake, Mic, Headphones } from 'lucide-react';
import { ScrollReveal, useParallax } from '../hooks/useAnimations';
import { getSeasonalLogo } from '../lib/seasonalLogo';
import api from '../lib/api';

const DJ_IMG = "https://customer-assets.emergentagent.com/job_7d11243e-bfc1-42ab-ab3b-8c95e25ccd72/artifacts/70vi058x_pexels-david-bares-42311-323687.jpg";
const MOCKTAIL_IMG = "https://customer-assets.emergentagent.com/job_7d11243e-bfc1-42ab-ab3b-8c95e25ccd72/artifacts/gjzszyyd_pexels-willians_photography-2157111846-34684084.jpg";
const SERVER_IMG = "https://customer-assets.emergentagent.com/job_7d11243e-bfc1-42ab-ab3b-8c95e25ccd72/artifacts/qk9eetk2_pexels-rdne-4921020.jpg";
const ABOUT_IMG = "https://images.unsplash.com/photo-1651482123980-fa4625df46cf?w=800&q=80";
const HERO_IMG = "https://images.pexels.com/photos/3101521/pexels-photo-3101521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LOGO_URL = getSeasonalLogo();

const partners = [
  { name: "Miss Naturelle Poitou-Charentes", logo: "/logos/partners/miss-naturelle.jpeg" },
];

const INSTAGRAM_USERNAME = 'rsxconcept';

const KARAOKE_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mocktail-hub-6/artifacts/yjyqeazz_FA9B1528-C89D-4445-985E-95156B6DD1D5.png";
const BLINDTEST_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mocktail-hub-6/artifacts/d9djihcf_2D11CB4D-D692-430A-9D17-266F10B5B6B3.png";

const services = [
  { title: "DJ & Animation", desc: "Ambiance musicale sur mesure pour vos événements", details: ["DJ Set personnalisé", "Animation micro", "Son & lumière pro"], img: DJ_IMG, icon: Music, link: "/prestations#dj" },
  { title: "Blind Test Interactif", desc: "Compétition musicale sur smartphone en temps réel", details: ["Jouable sur smartphone", "Classement en direct", "Duels entre joueurs"], img: BLINDTEST_IMG, icon: Headphones, link: "/prestations#blindtest" },
  { title: "Karaoké KaraFun", desc: "60 000+ titres, micros pro, ambiance garantie", details: ["60 000+ titres", "Micros sans fil", "Paroles sur grand écran"], img: KARAOKE_IMG, icon: Mic, link: "/prestations#karaoke" },
  { title: "Bar à Mocktails", desc: "Cocktails sans alcool élégants et gourmands", details: ["Mocktails créatifs", "Recettes personnalisées", "Atelier Barman"], img: MOCKTAIL_IMG, icon: GlassWater, link: "/prestations#bar" },
  { title: "Serveur à Domicile", desc: "Service professionnel pour vos réceptions", details: ["Mise en place complète", "Service à table", "Débarrassage"], img: SERVER_IMG, icon: ConciergeBell, link: "/prestations#serveur" },
];

const whyUs = [
  { icon: Sparkles, title: "Sur-mesure", desc: "Chaque prestation adaptée à vos envies" },
  { icon: Music, title: "Matériel Pro", desc: "Son & lumières professionnels" },
  { icon: Timer, title: "Ponctualité", desc: "Installation et service dans les temps" },
  { icon: Smile, title: "Ambiance Garantie", desc: "Des soirées mémorables assurées" },
  { icon: FileText, title: "Devis Rapide", desc: "Réponse sous 24-48h" },
  { icon: PartyPopper, title: "Tout Événement", desc: "Anniversaire, mariage, entreprise..." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [dbPartners, setDbPartners] = useState([]);
  const [heroRef, heroOffset] = useParallax();

  useEffect(() => {
    api.get('/events').then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get('/partners').then(r => setDbPartners(r.data)).catch(() => {});
    api.post('/seed').catch(() => {});
  }, []);

  const allPartners = dbPartners.length > 0 ? dbPartners : partners;

  return (
    <div>
      {/* Bienvenue */}
      <section className="py-16 md:py-24 px-4 md:px-8 max-w-5xl mx-auto text-center" data-testid="welcome-section">
        <ScrollReveal>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>BIENVENUE CHEZ RSX</h2>
          <p className="text-base text-[#A1A1AA] mb-2">{"Commençons par faire connaissance."}</p>
          <p className="text-sm text-[#A1A1AA] mb-10">{"Afin de bien vous guider, nous aurions besoin de savoir si vous êtes :"}</p>
        </ScrollReveal>
        <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            { to: "/reservation", icon: User, t: "Particulier", d: "Anniversaire, mariage, fête privée..." },
            { to: "/entreprises", icon: Building2, t: "Entreprise", d: "Séminaire, team building, événement corporate..." },
            { to: "/associations", icon: Users, t: "Association / Comité", d: "Gala, kermesse, événement associatif..." },
          ].map((item, i) => (
            <ScrollReveal key={item.t} delay={i * 0.12} scale>
              <Link to={item.to} className="card-hover apple-press bg-[#121212] border border-white/5 rounded-2xl p-6 text-center block" data-testid={`welcome-${item.t.toLowerCase().split(' ')[0]}`}>
                <item.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>{item.t}</h3>
                <p className="text-xs text-[#A1A1AA]">{item.d}</p>
              </Link>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={0.4}>
          <div className="mt-6">
            <Link to="/carte" className="text-sm text-[#D4AF37] hover:text-[#F9E5C0] inline-flex items-center gap-2 apple-link" data-testid="welcome-carte">
              <ClipboardList className="w-4 h-4" /> Voir la carte des Mocktails
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* Hero - Apple parallax */}
      <section ref={heroRef} className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Event" className="absolute inset-0 w-full h-full object-cover apple-zoom"
          style={{ transform: `scale(1.1) translateY(${heroOffset * 0.3}px)` }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <ScrollReveal>
            <img src={LOGO_URL} alt="RSX Concept" className="w-20 h-20 rounded-full mx-auto mb-6 gold-glow" />
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">{"Service – DJ – Bar"}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.25}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
              {"Vivez des événements "}<span className="text-[#D4AF37] font-medium">{"mémorables"}</span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.35}>
            <p className="text-base md:text-lg text-[#A1A1AA] mb-8 max-w-xl mx-auto leading-relaxed">
              {"RSX CONCEPT anime vos soirées avec un DJ professionnel, un bar à mocktails innovant et un service à domicile de qualité. Une expérience sur mesure, conviviale et professionnelle."}
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.45}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/reservation" data-testid="hero-devis-btn"><Button className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-8 py-3 text-sm font-semibold apple-press">Demander un devis</Button></Link>
              <Link to="/prestations" data-testid="hero-prestations-btn"><Button variant="outline" className="border-white/20 text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-8 py-3 text-sm apple-press">{"Découvrir nos prestations"}</Button></Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Nos Prestations */}
      <section className="py-16 md:py-24 px-4 md:px-8 max-w-6xl mx-auto" data-testid="services-section">
        <ScrollReveal>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] text-center mb-2">{"Découvrir"}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] text-center mb-2">Nos Prestations</p>
          <h2 className="text-2xl md:text-3xl font-light tracking-tight text-center mb-3" style={{ fontFamily: 'Outfit' }}>Des services sur mesure</h2>
          <p className="text-sm text-[#A1A1AA] text-center mb-12">{"Cinq expertises complémentaires pour faire de votre événement un moment unique"}</p>
        </ScrollReveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <ScrollReveal key={s.title} delay={i * 0.1} scale>
              <Link to={s.link || '/prestations'}
                className="card-hover apple-press bg-[#121212] border border-white/5 rounded-2xl overflow-hidden block">
                <div className="relative h-52 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover apple-zoom" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  <div className="absolute bottom-3 left-3 bg-[#D4AF37]/15 backdrop-blur-md p-2 rounded-xl"><s.icon className="w-5 h-5 text-[#D4AF37]" /></div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                  <p className="text-sm text-[#A1A1AA] mb-3">{s.desc}</p>
                  <div className="space-y-1">
                    {s.details.map(d => (<div key={d} className="text-xs text-[#D4AF37] flex items-center gap-2"><div className="w-1 h-1 bg-[#D4AF37] rounded-full" />{d}</div>))}
                  </div>
                  <p className="text-xs text-[#D4AF37] mt-3 flex items-center gap-1">En savoir plus <ChevronRight className="w-3 h-3" /></p>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={0.5}>
          <div className="text-center mt-8">
            <Link to="/prestations" className="text-sm text-[#D4AF37] hover:text-[#F9E5C0] inline-flex items-center gap-1 apple-link">{"Voir tous nos services en détail"} <ChevronRight className="w-4 h-4" /></Link>
          </div>
        </ScrollReveal>
      </section>

      {/* Pourquoi RSX */}
      <section className="py-16 px-4 md:px-8 max-w-6xl mx-auto" data-testid="why-section">
        <ScrollReveal>
          <h2 className="text-2xl font-light tracking-tight text-center mb-10" style={{ fontFamily: 'Outfit' }}>Pourquoi choisir <span className="text-[#D4AF37]">RSX CONCEPT</span> ?</h2>
        </ScrollReveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {whyUs.map((w, i) => (
            <ScrollReveal key={w.title} delay={i * 0.08} scale>
              <div className="bg-[#121212] border border-white/5 rounded-2xl p-5 flex items-start gap-4 card-hover">
                <div className="bg-[#D4AF37]/10 p-2.5 rounded-xl shrink-0"><w.icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <div><h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>{w.title}</h3><p className="text-xs text-[#A1A1AA]">{w.desc}</p></div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-16 px-4 md:px-8 max-w-4xl mx-auto" data-testid="howto-section">
        <ScrollReveal>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] text-center mb-2">{"Comment ça marche"}</p>
          <h2 className="text-2xl md:text-3xl font-light tracking-tight text-center mb-3" style={{ fontFamily: 'Outfit' }}>Simple comme <span className="text-[#D4AF37]">1, 2, 3</span></h2>
          <p className="text-sm text-[#A1A1AA] text-center mb-12">{"De votre demande à la réalisation, nous simplifions tout"}</p>
        </ScrollReveal>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: "01", title: "Décrivez votre événement", desc: "Parlez-nous de votre projet : date, lieu, nombre d'invités, ambiance souhaitée." },
            { step: "02", title: "Recevez votre devis", desc: "Sous 24-48h, recevez une proposition personnalisée et détaillée." },
            { step: "03", title: "Profitez de la fête", desc: "Le jour J, on s'occupe de tout. Vous n'avez plus qu'à profiter !" },
          ].map((s, i) => (
            <ScrollReveal key={s.step} delay={i * 0.2}>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37]/20 mb-3" style={{ fontFamily: 'Outfit', transition: 'color 0.5s' }}>{s.step}</div>
                <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={0.6}>
          <div className="text-center mt-10">
            <Link to="/reservation"><Button className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-8 py-3 text-sm font-semibold apple-press" data-testid="howto-cta">Commencer maintenant</Button></Link>
          </div>
        </ScrollReveal>
      </section>

      {/* À propos */}
      <section className="py-16 px-4 md:px-8 max-w-6xl mx-auto" data-testid="about-section">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <ScrollReveal direction="left" scale>
            <div className="relative rounded-2xl overflow-hidden h-80">
              <img src={ABOUT_IMG} alt="À propos" className="w-full h-full object-cover apple-zoom" />
              <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-lg">
                <span className="text-lg font-bold text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>2025</span>
                <span className="text-[10px] text-[#A1A1AA] block">{"À votre service"}</span>
              </div>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="right">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{"À propos de nous"}</p>
              <h2 className="text-2xl md:text-3xl font-light tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>RSX <span className="text-[#D4AF37]">CONCEPT</span></h2>
              <p className="text-[#A1A1AA] leading-relaxed mb-4">{"RSX CONCEPT se spécialise dans la création de moments inoubliables grâce à une combinaison unique de services : musique entraînante, cocktails raffinés et service de qualité."}</p>
              <p className="text-[#A1A1AA] leading-relaxed mb-6">{"Notre mission est de rendre chaque événement mémorable, de la planification à l'exécution. Que ce soit pour un anniversaire, un mariage ou un événement d'entreprise, nous mettons notre expertise à votre service."}</p>
              <div className="grid grid-cols-2 gap-3">
                {["Expérience dans l'événementiel", "Approche personnalisée", "Matériel professionnel", "Service client réactif"].map((item, i) => (
                  <ScrollReveal key={item} delay={0.1 + i * 0.08}>
                    <div className="flex items-center gap-2 text-sm"><Zap className="w-3.5 h-3.5 text-[#D4AF37]" />{item}</div>
                  </ScrollReveal>
                ))}
              </div>
              <div className="mt-6"><Link to="/contact"><Button variant="outline" className="border-white/20 text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-6 text-sm apple-press" data-testid="about-contact">Contactez-nous</Button></Link></div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Events Preview */}
      {events.length > 0 && (
        <section className="py-16 px-4 md:px-8 max-w-6xl mx-auto" data-testid="events-preview">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-8">
              <div><h2 className="text-base md:text-lg text-[#D4AF37] font-medium mb-1" style={{ fontFamily: 'Outfit' }}>{"À venir"}</h2><p className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Outfit' }}>{"Prochains événements"}</p></div>
              <Link to="/events" className="text-sm text-[#D4AF37] hover:text-[#F9E5C0] flex items-center gap-1 apple-link">Tout voir <ChevronRight className="w-4 h-4" /></Link>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-5">
            {events.map((evt, i) => (
              <ScrollReveal key={evt.event_id} delay={i * 0.12} scale>
                <Link to={`/events/${evt.event_id}`} className="card-hover apple-press bg-[#121212] border border-white/5 rounded-2xl p-5 block">
                  <div className="text-xs text-[#D4AF37] font-medium mb-2">{new Date(evt.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{evt.title}</h3>
                  <p className="text-sm text-[#A1A1AA] line-clamp-2 mb-3">{evt.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#A1A1AA]">{evt.location}</span>
                    {evt.price > 0 ? <span className="text-sm font-semibold text-[#D4AF37]">{evt.price.toFixed(2)} {"€"}</span> : <span className="text-xs text-[#10B981] font-medium">Gratuit</span>}
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* RSX Party Live */}
      <section className="py-16 px-4 md:px-8 max-w-5xl mx-auto" data-testid="party-live-section">
        <ScrollReveal scale>
          <div className="rounded-3xl p-8 md:p-12 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(59,130,246,0.08) 100%)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(220,38,38,0.1) 0%, transparent 50%), radial-gradient(ellipse at 80% 50%, rgba(59,130,246,0.08) 0%, transparent 50%)' }} />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full" style={{ boxShadow: '0 0 10px rgba(220,38,38,0.6)' }} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-red-400">Nouveau</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-3" style={{ fontFamily: 'Outfit', background: 'linear-gradient(135deg, #fff, #DC2626, #3B82F6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>RSX PARTY LIVE</h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  {"Animez vos soirées avec un blind test et quiz interactif ! Les invités rejoignent la partie avec leur téléphone et répondent en direct. Parfait pour anniversaires, soirées privées, événements d'entreprise."}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["Blind Test", "Quiz", "Karaoké", "Duels", "Équipes"].map(m => (
                    <span key={m} className="text-xs px-3 py-1 rounded-full bg-white/5 text-[#A1A1AA]">{m}</span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <a href="/api/blindtest/" data-testid="home-party-btn">
                    <Button className="rounded-full px-6 font-bold apple-press" style={{ background: 'linear-gradient(135deg, #DC2626, #3B82F6)', boxShadow: '0 0 20px rgba(220,38,38,0.2)' }}>
                      {"Découvrir"} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </a>
                  <Link to="/reservation">
                    <Button variant="outline" className="border-white/10 hover:border-red-400 text-white rounded-full px-6">{"Demander un devis"}</Button>
                  </Link>
                </div>
              </div>
              <div className="shrink-0 text-center">
                <div className="w-36 h-36 rounded-3xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(220,38,38,0.15), rgba(59,130,246,0.15))' }}>
                  <PartyPopper className="w-16 h-16" style={{ color: '#DC2626', filter: 'drop-shadow(0 0 20px rgba(220,38,38,0.4))' }} />
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Ils nous ont fait confiance */}
      <section className="py-16 px-4 md:px-8 max-w-5xl mx-auto" data-testid="partners-section">
        <ScrollReveal>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Handshake className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Nos partenaires</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Ils nous ont fait <span className="text-[#D4AF37]">confiance</span>
            </h2>
          </div>
        </ScrollReveal>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
          {allPartners.map((p, i) => (
            <ScrollReveal key={p.name || p.id} delay={i * 0.15} scale>
              <a href={p.url || '#'} target={p.url ? '_blank' : '_self'} rel="noopener noreferrer"
                className="group flex flex-col items-center gap-3" data-testid={`partner-${i}`}>
                <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-[#121212] border border-white/5 p-4 flex items-center justify-center card-hover overflow-hidden">
                  <img src={p.logo} alt={p.name} className="w-full h-full object-contain filter brightness-90 group-hover:brightness-110 transition-all duration-300" />
                </div>
                <span className="text-xs text-[#A1A1AA] text-center max-w-[140px]">{p.name}</span>
              </a>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Instagram */}
      <section className="py-16 px-4 md:px-8 max-w-5xl mx-auto" data-testid="instagram-section">
        <ScrollReveal>
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Suivez-nous</p>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Retrouvez-nous sur <span className="text-[#D4AF37]">Instagram</span>
            </h2>
            <a href={`https://instagram.com/${INSTAGRAM_USERNAME}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] mt-2 transition-colors">
              @{INSTAGRAM_USERNAME}
            </a>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.2}>
          <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 text-center">
            <iframe
              src={`https://www.instagram.com/${INSTAGRAM_USERNAME}/embed`}
              width="100%"
              height="480"
              frameBorder="0"
              scrolling="no"
              allowTransparency="true"
              style={{ maxWidth: '540px', margin: '0 auto', display: 'block', borderRadius: '12px', background: '#000' }}
              title="Instagram RSX Concept"
            />
            <a href={`https://instagram.com/${INSTAGRAM_USERNAME}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-[#D4AF37] hover:text-[#F9E5C0] transition-colors apple-link">
              Voir toutes nos publications
            </a>
          </div>
        </ScrollReveal>
      </section>

      {/* Contact */}
      <section className="py-16 px-4 md:px-8 max-w-4xl mx-auto text-center" data-testid="contact-cta">
        <ScrollReveal scale>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Contact</p>
          <h2 className="text-2xl md:text-3xl font-light tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>Parlons de votre <span className="text-[#D4AF37]">projet</span></h2>
          <p className="text-sm text-[#A1A1AA] mb-8">{"Décrivez votre événement et recevez un devis personnalisé sous 24-48h"}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <Link to="/reservation"><Button className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-8 py-3 text-sm font-semibold apple-press" data-testid="cta-reservation">Demander un devis</Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-white/20 text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-8 py-3 text-sm apple-press" data-testid="cta-contact">Nous contacter</Button></Link>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 justify-center text-sm text-[#A1A1AA]">
            <a href="sms:+33610507293" className="hover:text-[#D4AF37]">06 10 50 72 93 (SMS uniquement)</a>
            <a href="mailto:rsxconceptpro@gmail.com" className="hover:text-[#D4AF37]">rsxconceptpro@gmail.com</a>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-3">{"Zone d'intervention : Saint-Sauvant et alentours (Vienne)"}</p>
        </ScrollReveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-4 md:px-8" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="RSX" className="h-8 w-8 rounded-full" /><span className="text-sm text-[#A1A1AA]">{"RSX Concept – Saint-Sauvant, Vienne (86)"}</span></div>
          <div className="flex gap-6 text-xs text-[#A1A1AA]">
            <a href="sms:+33610507293" className="hover:text-[#D4AF37]">06 10 50 72 93</a>
            <a href="mailto:rsxconceptpro@gmail.com" className="hover:text-[#D4AF37]">rsxconceptpro@gmail.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
